import axios from "axios";
import React, { useCallback, useEffect, useState } from "react";
import { GENERATE_REPORT } from "../../../api/apiUrls";
import { buildCondition } from "../../../static/hook";
import Select from "react-select";
import TodoTables from "./TodoTables";
import { packageCodes, paymentStatusOptions, stStatusOptions } from "../../../static/dropdown";

const TodoDashboard = () => {
  const [toDoRepoLoading, setToDoRepoLoading] = useState(false);
  const [toDoRepoData, setToDoRepoData] = useState(null);
  const [allRepoLoading, setAllRepoLoading] = useState(false);
  const [allRepoData, setAllRepoData] = useState(null);
  const [allPrimaryRepoLoading, setAllPrimaryRepoLoading] = useState(false);
  const [allPrimaryRepoData, setAllPrimaryRepoData] = useState(null);
  const [allAltRepoLoading, setAllAltRepoLoading] = useState(false);
  const [allAltRepoData, setAllAltRepoData] = useState(null);
  const [filterDropdown, setFilterDropdown] = useState({
    intakeOptions: [],
    categoryOptions: [],
  });

  const [filterOptions, setFilterOptions] = useState({
    package: [],
    intake: [],
    course: [],
    ststatus: [],
    category: [],
    paymentStatus: [],
  });

  const fetchBaseQuery = useCallback(async (query) => {
    if (!query?.trim()) {
      console.warn("No query found to execute");
      return null;
    }
    try {
      const { data } = await axios.post(GENERATE_REPORT, { query });
      return data || null;
    } catch (error) {
      console.error(
        "Error occurred in fetchBaseQuery:",
        error?.response?.data?.message || error.message || "Unknown error"
      );
      return null;
    }
  }, []);

  const fetchFilterDropdown = useCallback(async () => {
    try {
      const queries = {
        intakeOptions:
          "SELECT IntakeCode AS 'Code', Intakename AS 'Name' FROM intakemas",
        categoryOptions:
          "SELECT CatCode AS 'Code', CatName AS 'Name' FROM catmas",
      };

      const fetchPromises = Object.entries(queries).map(
        async ([key, query]) => {
          const data = await fetchBaseQuery(query);
          return { key, data };
        }
      );

      const results = await Promise.all(fetchPromises);

      const updatedDropdowns = results.reduce((acc, { key, data }) => {
        if (data?.length) {
          acc[key] = data.map((item) => ({
            label: item.NAME || "Unknown",
            value: item.CODE || "N/A",
          }));
        } else {
          console.warn(`No data received for ${key}`);
        }
        return acc;
      }, {});

      if (Object.keys(updatedDropdowns).length) {
        setFilterDropdown((prev) => ({ ...prev, ...updatedDropdowns }));
      }
    } catch (error) {
      console.error("Error fetching filter dropdown options:", error.message);
    }
  }, [fetchBaseQuery]);

  const fetchToDoReportsLocationWise = async () => {
    setToDoRepoLoading(true);
    try {
      const locationsList = await fetchBaseQuery(
        `SELECT DISTINCT locname, loccode FROM locmas WHERE loccode NOT IN (19, 16)`
      );

      if (!Array.isArray(locationsList) || locationsList.length === 0) {
        console.log("No records to execute");
        return [];
      }

      // Build all filters
      const filters = [];
      const conditions = [
        buildCondition("s.intakeCode", filterOptions.intake),
        buildCondition("s.packageCode", filterOptions.package),
        buildCondition("s.ststatus", filterOptions.ststatus),
        buildCondition("s.LocCode", filterOptions.location),
        buildCondition("s.coursetype", filterOptions.course),
        buildCondition("s.catCode", filterOptions.category),
      ];

      conditions.forEach((cond) => cond && filters.push(cond));

      const allFilters = [...filters];
      const whereClause = allFilters.length
        ? `AND  ${allFilters.join(" AND ")}`
        : ``;

      const baseQuery =
        "SELECT COUNT(DISTINCT s.StdCode) FROM StudentMas s LEFT JOIN mailboxmas mb ON (mb.mailbox = s.stdYgid OR mb.mailbox = s.altygemailid)";
      const primaryBaseQueryTwo =
        "SELECT COUNT(mb.uid) FROM StudentMas s LEFT JOIN mailboxmas mb ON mb.mailbox = s.stdYgid";
      const altBaseQueryTwo =
        "SELECT COUNT(mb.uid) FROM StudentMas s LEFT JOIN mailboxmas mb ON mb.mailbox = s.altygemailid";
      const queries = {
        allStudents: `${baseQuery} WHERE s.StdCode IS NOT NULL ${whereClause}`,
        primaryTotalMails: `${primaryBaseQueryTwo} WHERE s.StdCode IS NOT NULL ${whereClause}`,
        altTotalMails: `${altBaseQueryTwo} WHERE s.StdCode IS NOT NULL ${whereClause}`,
        primaryOpenMails: `${primaryBaseQueryTwo} WHERE mb.statuscode = 1 ${whereClause}`,
        altOpenMails: `${altBaseQueryTwo} WHERE mb.statuscode = 1 ${whereClause}`,
        primaryCloseMails: `${primaryBaseQueryTwo} WHERE mb.statuscode = 2 ${whereClause}`,
        altCloseMails: `${altBaseQueryTwo} WHERE mb.statuscode = 2 ${whereClause}`,
        primaryInProcessMails: `${primaryBaseQueryTwo} WHERE mb.statuscode = 3 ${whereClause}`,
        altInProcessMails: `${altBaseQueryTwo} WHERE mb.statuscode = 3 ${whereClause}`,
        primaryInProcessStudentMails: `${primaryBaseQueryTwo} WHERE mb.statuscode = 4 ${whereClause}`,
        altInProcessStudentMails: `${altBaseQueryTwo} WHERE mb.statuscode = 4 ${whereClause}`,
        primaryReOpenMails: `${primaryBaseQueryTwo} WHERE mb.statuscode = 5${whereClause}`,
        altReOpenMails: `${altBaseQueryTwo} WHERE mb.statuscode = 5 ${whereClause}`,
        primaryInfoSharedMails: `${primaryBaseQueryTwo} WHERE mb.statuscode = 6 ${whereClause}`,
        altInfoSharedMails: `${altBaseQueryTwo} WHERE mb.statuscode = 6 ${whereClause}`,
        primaryAcknMails: `${primaryBaseQueryTwo} WHERE mb.statuscode = 7 ${whereClause}`,
        altReAcknMails: `${altBaseQueryTwo} WHERE mb.statuscode = 7 ${whereClause}`,
      };

      const responseObj = [];

      for (const item of locationsList) {
        const location = item.LOCCODE;
        const locname = item?.LOCNAME;
        const todoData = { locname, details: {} };

        for (const [key, baseQuery] of Object.entries(queries)) {
          // Inject counsellor condition
          const finalQuery = `${baseQuery} AND s.loccode = ${location}`;

          try {
            const result = await fetchBaseQuery(finalQuery);
            todoData.details[key] = {
              query: finalQuery,
              count: result[0]?.Column1 || 0,
            };
          } catch (error) {
            console.error(`Error fetching ${key}:`, error);
            todoData.details[key] = null;
          }
        }

        responseObj.push(todoData);
      }
      if (responseObj) {
        setToDoRepoData(responseObj);
        setToDoRepoLoading(false);
      } else {
        setToDoRepoData(null);
      }
    } catch (error) {
      console.error("Failed to fetch fetchToDoReportsLocationWise", error);
      setToDoRepoLoading(false);
    } finally {
      setToDoRepoLoading(false);
    }
  };

  const fetchAllTodoReports = async () => {
    setAllRepoLoading(true);
    try {
      // Build all filters
      const filters = [];
      const conditions = [
        buildCondition("s.intakeCode", filterOptions.intake),
        buildCondition("s.packageCode", filterOptions.package),
        buildCondition("s.ststatus", filterOptions.ststatus),
        buildCondition("s.LocCode", filterOptions.location),
        buildCondition("s.coursetype", filterOptions.course),
        buildCondition("s.catCode", filterOptions.category),
      ];

      conditions.forEach((cond) => cond && filters.push(cond));

      const allFilters = [...filters];
      const whereClause = allFilters.length
        ? `AND  ${allFilters.join(" AND ")}`
        : ``;

      const queries = {
        allStudents: `SELECT COUNT(DISTINCT s.StdCode) FROM StudentMas s WHERE s.StdCode IS NOT NULL ${whereClause}`,
        primaryBlankMails: `SELECT COUNT(DISTINCT s.StdCode) FROM StudentMas s WHERE (s.stdYgid IS NULL OR s.stdYgid = '') ${whereClause}`,
        altBlankMails: `SELECT COUNT(DISTINCT s.StdCode) FROM StudentMas s WHERE (s.altygemailid IS NULL OR s.altygemailid = '') ${whereClause}`,
        primaryMails: `SELECT COUNT(DISTINCT s.StdCode) FROM StudentMas s WHERE s.stdYgid IS NOT NULL AND s.stdYgid != '' ${whereClause}`,
        altMails: `SELECT COUNT(DISTINCT s.StdCode) FROM StudentMas s WHERE s.altygemailid IS NOT NULL AND s.altygemailid != '' ${whereClause}`,
        bothMails: `SELECT COUNT(DISTINCT s.StdCode) FROM StudentMas s WHERE s.altygemailid IS NOT NULL AND s.altygemailid != '' AND s.stdYgid IS NOT NULL AND s.stdYgid != '' ${whereClause}`,
      };
      const responseObj = {};
      for (const query in queries) {
        const result = await fetchBaseQuery(queries[query]);
        responseObj[query] = {
          count: result[0]?.Column1,
          query: queries[query],
        };
      }
      const hasDocRatioData =
        responseObj &&
        typeof responseObj === "object" &&
        Object.keys(responseObj).length > 0;
      if (hasDocRatioData) {
        setAllRepoData(responseObj);
      }
    } catch (error) {
      console.error(error);
      setAllRepoLoading(false);
    } finally {
      setAllRepoLoading(false);
    }
  };

  const fetchAllPrimaryTodoReports = async () => {
    setAllPrimaryRepoLoading(true);
    try {
      // Build all filters
      const filters = [];
      const conditions = [
        buildCondition("s.intakeCode", filterOptions.intake),
        buildCondition("s.packageCode", filterOptions.package),
        buildCondition("s.ststatus", filterOptions.ststatus),
        buildCondition("s.LocCode", filterOptions.location),
        buildCondition("s.coursetype", filterOptions.course),
        buildCondition("s.catCode", filterOptions.category),
      ];

      conditions.forEach((cond) => cond && filters.push(cond));

      const allFilters = [...filters];
      const whereClause = allFilters.length
        ? `AND  ${allFilters.join(" AND ")}`
        : ``;
      const primaryBaseQueryTwo =
        "SELECT COUNT(mb.uid) FROM StudentMas s LEFT JOIN mailboxmas mb ON mb.mailbox = s.stdYgid";
      const queries = {
        totalEmail: `${primaryBaseQueryTwo} WHERE mb.uid IS NOT NULL ${whereClause}`,
        primaryOpenMails: `${primaryBaseQueryTwo} WHERE mb.statuscode = 1 ${whereClause}`,
        primaryCloseMails: `${primaryBaseQueryTwo} WHERE mb.statuscode = 2 ${whereClause}`,
        primaryInProcessMails: `${primaryBaseQueryTwo} WHERE mb.statuscode = 3 ${whereClause}`,
        primaryInProcessStudentMails: `${primaryBaseQueryTwo} WHERE mb.statuscode = 4 ${whereClause}`,
        primaryReOpenMails: `${primaryBaseQueryTwo} WHERE mb.statuscode = 5${whereClause}`,
        primaryInfoSharedMails: `${primaryBaseQueryTwo} WHERE mb.statuscode = 6 ${whereClause}`,
        primaryAcknMails: `${primaryBaseQueryTwo} WHERE mb.statuscode = 7 ${whereClause}`,
      };
      const responseObj = {};
      for (const query in queries) {
        const result = await fetchBaseQuery(queries[query]);
        responseObj[query] = {
          count: result[0]?.Column1,
          query: queries[query],
        };
      }
      const hasDocRatioData =
        responseObj &&
        typeof responseObj === "object" &&
        Object.keys(responseObj).length > 0;
      if (hasDocRatioData) {
        setAllPrimaryRepoData(responseObj);
        console.log("first", responseObj);
      }
    } catch (error) {
      console.error(error);
      setAllPrimaryRepoLoading(false);
    } finally {
      setAllPrimaryRepoLoading(false);
    }
  };
  const fetchAllSecondaryTodoReports = async () => {
    setAllAltRepoLoading(true);
    try {
      // Build all filters
      const filters = [];
      const conditions = [
        buildCondition("s.intakeCode", filterOptions.intake),
        buildCondition("s.packageCode", filterOptions.package),
        buildCondition("s.ststatus", filterOptions.ststatus),
        buildCondition("s.LocCode", filterOptions.location),
        buildCondition("s.coursetype", filterOptions.course),
        buildCondition("s.catCode", filterOptions.category),
      ];

      conditions.forEach((cond) => cond && filters.push(cond));

      const allFilters = [...filters];
      const whereClause = allFilters.length
        ? `AND  ${allFilters.join(" AND ")}`
        : ``;
      const altBaseQueryTwo =
        "SELECT COUNT(mb.uid) FROM StudentMas s LEFT JOIN mailboxmas mb ON mb.mailbox = s.altygemailid";
      const queries = {
        totalEmail: `${altBaseQueryTwo} WHERE mb.uid IS NOT NULL ${whereClause}`,
        primaryOpenMails: `${altBaseQueryTwo} WHERE mb.statuscode = 1 ${whereClause}`,
        primaryCloseMails: `${altBaseQueryTwo} WHERE mb.statuscode = 2 ${whereClause}`,
        primaryInProcessMails: `${altBaseQueryTwo} WHERE mb.statuscode = 3 ${whereClause}`,
        primaryInProcessStudentMails: `${altBaseQueryTwo} WHERE mb.statuscode = 4 ${whereClause}`,
        primaryReOpenMails: `${altBaseQueryTwo} WHERE mb.statuscode = 5${whereClause}`,
        primaryInfoSharedMails: `${altBaseQueryTwo} WHERE mb.statuscode = 6 ${whereClause}`,
        primaryAcknMails: `${altBaseQueryTwo} WHERE mb.statuscode = 7 ${whereClause}`,
      };
      const responseObj = {};
      for (const query in queries) {
        const result = await fetchBaseQuery(queries[query]);
        responseObj[query] = {
          count: result[0]?.Column1,
          query: queries[query],
        };
      }
      const hasDocRatioData =
        responseObj &&
        typeof responseObj === "object" &&
        Object.keys(responseObj).length > 0;
      if (hasDocRatioData) {
        console.log("first", responseObj);
      }
    } catch (error) {
      console.error(error);
      setAllAltRepoLoading(false);
    } finally {
      setAllAltRepoLoading(false);
    }
  };

  const handleFilterChange = () => {
    fetchToDoReportsLocationWise();
  };

  useEffect(() => {
    fetchFilterDropdown();
    fetchToDoReportsLocationWise();
    fetchAllTodoReports();
    fetchAllPrimaryTodoReports();
  }, []);
  return (
    <div>
      <div className="header py-3 border border-light px-2 rounded bg-light">
        <h5>Filter</h5>
        <div className="row g-2 g-lg-3 mb-3">
          <div className="col-md-2">
            <div className="form-group">
              <Select
                id="intake-select"
                options={filterDropdown.intakeOptions}
                isMulti
                isClearable
                placeholder="Select Intake"
                onChange={(selectedOptions) => {
                  setFilterOptions((prev) => ({
                    ...prev,
                    intake: selectedOptions
                      ? selectedOptions.map((opt) => parseInt(opt.value))
                      : [],
                  }));
                }}
              />
            </div>
          </div>
          <div className="col-md-2">
            <div className="form-group">
              <Select
                id="ststatus-select"
                options={stStatusOptions}
                isMulti
                isClearable
                placeholder="Select Student Status"
                onChange={(selectedOptions) => {
                  setFilterOptions((prev) => ({
                    ...prev,
                    ststatus: selectedOptions
                      ? selectedOptions.map((opt) => parseInt(opt.value))
                      : [],
                  }));
                }}
              />
            </div>
          </div>
          <div className="col-md-2">
            <div className="form-group">
              <Select
                id="ststatus-select"
                options={paymentStatusOptions}
                isMulti
                isClearable
                placeholder="Select Payment Status"
                onChange={(selectedOptions) => {
                  setFilterOptions((prev) => ({
                    ...prev,
                    paymentStatus: selectedOptions
                      ? selectedOptions.map((opt) => opt.value)
                      : [],
                  }));
                }}
              />
            </div>
          </div>
          <div className="col-md-2">
            <div className="form-group">
              <Select
                id="category-select"
                options={filterDropdown.categoryOptions}
                isMulti
                isClearable
                placeholder="Select Student Category"
                onChange={(selectedOptions) => {
                  setFilterOptions((prev) => ({
                    ...prev,
                    category: selectedOptions
                      ? selectedOptions.map((opt) => parseInt(opt.value))
                      : [],
                  }));
                }}
              />
            </div>
          </div>
          <div className="col-md-2">
            <div className="form-group">
              <Select
                id="package-select"
                options={packageCodes}
                isMulti
                isClearable
                placeholder="Select Package"
                onChange={(selectedOptions) => {
                  setFilterOptions((prev) => ({
                    ...prev,
                    package: selectedOptions
                      ? selectedOptions.map((opt) => parseInt(opt.value))
                      : [],
                  }));
                }}
              />
            </div>
          </div>

          <div className="col-md-2">
            <div className="form-group">
              <Select
                id="course-select"
                options={[
                  { label: "Master's", value: "M" },
                  { label: "Bachelor's", value: "B" },
                ]}
                isMulti
                isClearable
                placeholder="Select Course"
                onChange={(selectedOptions) => {
                  setFilterOptions((prev) => ({
                    ...prev,
                    course: selectedOptions
                      ? selectedOptions.map((opt) => opt.value)
                      : [],
                  }));
                }}
              />
            </div>
          </div>
          <div className="form-group col-md-2">
            <button
              type="button"
              className="btn btn-warning btn-sm"
              onClick={handleFilterChange}
            >
              Apply Filter
            </button>
          </div>
        </div>
      </div>
      <TodoTables
        toDoRepoLoading={toDoRepoLoading}
        toDoRepoData={toDoRepoData}
        allRepoLoading={allRepoLoading}
        allRepoData={allRepoData}
        allPrimaryRepoLoading={allPrimaryRepoLoading}
        allPrimaryRepoData={allPrimaryRepoData}
      />
    </div>
  );
};

export default TodoDashboard;
